import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  LayoutDashboard,
  FolderKanban,
  Users,
  Calendar,
  Settings,
  Sparkles,
  BarChart3,
  Briefcase,
  Star,
  Clock,
  Plus,
} from 'lucide-react';
import { useProjects } from '@/hooks/useProjects';
import { useFavoriteProjects } from '@/hooks/useFavoriteProjects';
import { useRecentProjects } from '@/hooks/useRecentProjects';

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const NAV_ITEMS = [
  { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard, keywords: 'inicio home' },
  { label: 'Proyectos', path: '/projects', icon: FolderKanban, keywords: 'projects lista' },
  { label: 'Equipo', path: '/team', icon: Users, keywords: 'team miembros' },
  { label: 'Mi Día', path: '/my-day', icon: Calendar, keywords: 'myday hoy tareas' },
  { label: 'Calendario', path: '/calendar', icon: Calendar, keywords: 'calendar' },
  { label: 'Análisis', path: '/analytics', icon: BarChart3, keywords: 'analytics reportes' },
  { label: 'IA Predictiva', path: '/ai-predictive', icon: Sparkles, keywords: 'ai predictive valen' },
  { label: 'Configuración', path: '/settings', icon: Settings, keywords: 'settings ajustes' },
];

export const CommandPalette: React.FC<CommandPaletteProps> = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const { projects } = useProjects();
  const { favoriteIds } = useFavoriteProjects();
  const { recents } = useRecentProjects();

  const favoriteProjects = useMemo(
    () => projects.filter((p) => favoriteIds.includes(p.id)),
    [projects, favoriteIds]
  );

  const otherProjects = useMemo(
    () =>
      projects
        .filter((p) => !favoriteIds.includes(p.id) && !recents.some((r) => r.id === p.id))
        .slice(0, 15),
    [projects, favoriteIds, recents]
  );

  const go = (path: string) => {
    onOpenChange(false);
    navigate(path);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Buscar proyectos, navegar o ejecutar acciones…" />
      <CommandList>
        <CommandEmpty>Sin resultados.</CommandEmpty>

        <CommandGroup heading="Acciones rápidas">
          <CommandItem onSelect={() => go('/projects')}>
            <Plus className="mr-2 h-4 w-4" />
            Nuevo proyecto
            <span className="ml-auto text-xs text-muted-foreground">Ir a Proyectos</span>
          </CommandItem>
          <CommandItem onSelect={() => go('/my-day')}>
            <Calendar className="mr-2 h-4 w-4" />
            Mi Día — tareas de hoy
          </CommandItem>
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Navegación">
          {NAV_ITEMS.map((item) => (
            <CommandItem
              key={item.path}
              value={`${item.label} ${item.keywords}`}
              onSelect={() => go(item.path)}
            >
              <item.icon className="mr-2 h-4 w-4" />
              {item.label}
            </CommandItem>
          ))}
        </CommandGroup>

        {favoriteProjects.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="⭐ Favoritos">
              {favoriteProjects.map((p) => (
                <CommandItem
                  key={p.id}
                  value={`fav ${p.name} ${p.description || ''}`}
                  onSelect={() => go(`/project-management/${p.id}`)}
                >
                  <Star className="mr-2 h-4 w-4 fill-yellow-400 text-yellow-500" />
                  {p.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {recents.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Recientes">
              {recents.map((r) => (
                <CommandItem
                  key={r.id}
                  value={`recent ${r.name}`}
                  onSelect={() => go(`/project-management/${r.id}`)}
                >
                  <Clock className="mr-2 h-4 w-4" />
                  {r.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {otherProjects.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Proyectos">
              {otherProjects.map((p) => (
                <CommandItem
                  key={p.id}
                  value={`${p.name} ${p.description || ''}`}
                  onSelect={() => go(`/project-management/${p.id}`)}
                >
                  <Briefcase className="mr-2 h-4 w-4" />
                  {p.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
};

/** Hook que registra Cmd/Ctrl+K para abrir un palette controlado externamente. */
export const useCommandPalette = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return { open, setOpen };
};
